import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Target,
  Layers,
  Brain,
  Database,
  Settings,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Play,
} from 'lucide-react';
import {
  TAO6TaskType,
  TAO6PretrainedModel,
  TAO6Dataset,
} from '@/mock/gpu-instance';
import TAO6ModelSelector from './TAO6ModelSelector';
import TAO6DatasetManager from './TAO6DatasetManager';
import TAO6ConfigBuilder, { TAO6TrainingConfig } from './TAO6ConfigBuilder';

interface TAO6ExperimentCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  models: TAO6PretrainedModel[];
  datasets: TAO6Dataset[];
  onCreateExperiment: (experiment: {
    name: string;
    task: TAO6TaskType;
    model: TAO6PretrainedModel;
    dataset: TAO6Dataset;
    config: TAO6TrainingConfig;
  }) => void;
}

const STEPS = [
  { id: 'task', title: 'Select Task', icon: Target },
  { id: 'model', title: 'Choose Model', icon: Brain },
  { id: 'dataset', title: 'Select Dataset', icon: Database },
  { id: 'config', title: 'Configure Training', icon: Settings },
  { id: 'review', title: 'Review & Create', icon: CheckCircle2 },
];

const DEFAULT_CONFIG: TAO6TrainingConfig = {
  epochs: 50,
  batchSize: 16,
  learningRate: 0.001,
  optimizer: 'adamw',
  augmentations: ['random_flip', 'random_crop'],
  enableQAT: false,
  enableKD: false,
  multiGPU: false,
  gpuCount: 1,
};

const TAO6ExperimentCreator = ({
  open,
  onOpenChange,
  models,
  datasets,
  onCreateExperiment,
}: TAO6ExperimentCreatorProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTask, setSelectedTask] = useState<TAO6TaskType | null>(null);
  const [selectedModel, setSelectedModel] = useState<TAO6PretrainedModel | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<TAO6Dataset | null>(null);
  const [config, setConfig] = useState<TAO6TrainingConfig>(DEFAULT_CONFIG);
  const [experimentName, setExperimentName] = useState('');

  const handleReset = () => {
    setCurrentStep(0);
    setSelectedTask(null);
    setSelectedModel(null);
    setSelectedDataset(null);
    setConfig(DEFAULT_CONFIG);
    setExperimentName('');
  };

  const handleClose = () => {
    onOpenChange(false);
    handleReset();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedTask !== null;
      case 1:
        return selectedModel !== null;
      case 2:
        return selectedDataset !== null;
      case 3:
        return true;
      case 4:
        return experimentName.trim().length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed()) {
      setCurrentStep(currentStep + 1);
      // Auto-generate experiment name on last step
      if (currentStep === 3 && !experimentName && selectedModel && selectedDataset) {
        setExperimentName(
          `${selectedModel.name}_${selectedDataset.name.toLowerCase().replace(/\s+/g, '_')}`
        );
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreate = () => {
    if (selectedTask && selectedModel && selectedDataset && experimentName) {
      onCreateExperiment({
        name: experimentName,
        task: selectedTask,
        model: selectedModel,
        dataset: selectedDataset,
        config,
      });
      handleClose();
    }
  };

  const filteredModels = selectedTask
    ? models.filter((m) => m.task === selectedTask)
    : models;

  const filteredDatasets = selectedTask
    ? datasets.filter((d) => d.task === selectedTask)
    : datasets;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-green-500" />
            Create New Experiment
          </DialogTitle>
          <DialogDescription>
            Set up a new training experiment with TAO 6.x
          </DialogDescription>
        </DialogHeader>

        {/* Steps Progress */}
        <div className="flex items-center justify-between py-4 border-b">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <div
                key={step.id}
                className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div
                  className={`flex items-center gap-2 ${
                    isActive
                      ? 'text-primary'
                      : isCompleted
                      ? 'text-green-600'
                      : 'text-muted-foreground'
                  }`}
                >
                  <div
                    className={`p-2 rounded-full ${
                      isActive
                        ? 'bg-primary/10'
                        : isCompleted
                        ? 'bg-green-500/10'
                        : 'bg-muted'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <StepIcon className="h-4 w-4" />
                    )}
                  </div>
                  <span className="text-sm font-medium hidden md:block">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="py-4 min-h-[400px]">
          {/* Step 1: Select Task */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">What do you want to train?</div>
              <div className="grid grid-cols-2 gap-4">
                <Card
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTask === 'object_detection'
                      ? 'border-primary ring-1 ring-primary'
                      : ''
                  }`}
                  onClick={() => setSelectedTask('object_detection')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-full bg-green-500/10 w-fit mx-auto mb-4">
                      <Target className="h-8 w-8 text-green-500" />
                    </div>
                    <div className="font-medium text-lg mb-2">Object Detection</div>
                    <div className="text-sm text-muted-foreground">
                      Detect and locate objects in images with bounding boxes
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-1">
                      <Badge variant="secondary" className="text-xs">YOLOv8</Badge>
                      <Badge variant="secondary" className="text-xs">DINO</Badge>
                      <Badge variant="secondary" className="text-xs">RT-DETR</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTask === 'image_classification'
                      ? 'border-primary ring-1 ring-primary'
                      : ''
                  }`}
                  onClick={() => setSelectedTask('image_classification')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-full bg-blue-500/10 w-fit mx-auto mb-4">
                      <Layers className="h-8 w-8 text-blue-500" />
                    </div>
                    <div className="font-medium text-lg mb-2">Image Classification</div>
                    <div className="text-sm text-muted-foreground">
                      Classify images into predefined categories
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-1">
                      <Badge variant="secondary" className="text-xs">ResNet</Badge>
                      <Badge variant="secondary" className="text-xs">EfficientNet</Badge>
                      <Badge variant="secondary" className="text-xs">ViT</Badge>
                      <Badge variant="secondary" className="text-xs">Swin</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Choose Model */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Choose a Pretrained Model</div>
              <TAO6ModelSelector
                models={filteredModels}
                selectedModelId={selectedModel?.id}
                onSelectModel={(model) => {
                  setSelectedModel(model);
                  // Update augmentations based on model
                  setConfig((prev) => ({
                    ...prev,
                    augmentations: model.supportedAugmentations.slice(0, 3),
                  }));
                }}
                filterTask={selectedTask || undefined}
              />
            </div>
          )}

          {/* Step 3: Select Dataset */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Select a Dataset</div>
              <TAO6DatasetManager
                datasets={filteredDatasets}
                selectedDatasetId={selectedDataset?.id}
                onSelectDataset={setSelectedDataset}
              />
            </div>
          )}

          {/* Step 4: Configure Training */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Configure Training</div>
              <TAO6ConfigBuilder
                config={config}
                onChange={setConfig}
                selectedModel={selectedModel || undefined}
              />
            </div>
          )}

          {/* Step 5: Review & Create */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Review & Create</div>

              {/* Experiment Name */}
              <Card>
                <CardContent className="pt-4">
                  <Label>Experiment Name</Label>
                  <Input
                    value={experimentName}
                    onChange={(e) => setExperimentName(e.target.value)}
                    placeholder="my_experiment"
                    className="mt-2 font-mono"
                  />
                </CardContent>
              </Card>

              {/* Summary */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Configuration Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Task & Model */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Task</div>
                      <div className="flex items-center gap-2 mt-1">
                        {selectedTask === 'object_detection' ? (
                          <>
                            <Target className="h-4 w-4 text-green-500" />
                            <span>Object Detection</span>
                          </>
                        ) : (
                          <>
                            <Layers className="h-4 w-4 text-blue-500" />
                            <span>Image Classification</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Model</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Brain className="h-4 w-4" />
                        <span>{selectedModel?.displayName}</span>
                      </div>
                    </div>
                  </div>

                  {/* Dataset */}
                  <div>
                    <div className="text-sm text-muted-foreground">Dataset</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Database className="h-4 w-4" />
                      <span>{selectedDataset?.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {selectedDataset?.trainCount.toLocaleString()} train
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {selectedDataset?.valCount.toLocaleString()} val
                      </Badge>
                    </div>
                  </div>

                  {/* Training Config */}
                  <div className="grid grid-cols-4 gap-2 pt-2 border-t">
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">Epochs</div>
                      <div className="font-mono font-medium">{config.epochs}</div>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">Batch</div>
                      <div className="font-mono font-medium">{config.batchSize}</div>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">LR</div>
                      <div className="font-mono font-medium">{config.learningRate}</div>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">Optimizer</div>
                      <div className="font-mono font-medium capitalize">{config.optimizer}</div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {config.augmentations.length > 0 && (
                      <Badge variant="outline">
                        {config.augmentations.length} augmentations
                      </Badge>
                    )}
                    {config.enableQAT && (
                      <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                        QAT
                      </Badge>
                    )}
                    {config.enableKD && (
                      <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                        Knowledge Distillation
                      </Badge>
                    )}
                    {config.multiGPU && (
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                        {config.gpuCount} GPUs
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={handleNext} disabled={!canProceed()}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleCreate}
                disabled={!canProceed()}
                className="bg-green-600 hover:bg-green-700"
              >
                <Play className="h-4 w-4 mr-1" />
                Create Experiment
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TAO6ExperimentCreator;
